import { cn } from '@/lib/utils'
import MetricsPicker from '../../MetricsPicker'
import { ChevronDown } from 'lucide-react'
import MinusIcon from '@/components/icons/MinusIcon'
import { useState, useEffect } from 'react'
import TableRowSkeleton from '@/components/common/TableRowSkeleton'
import { useColumnPreferences } from '../../hooks/useColumnPreferences'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { PLATFORM_PLACEMENT_OPTIONS, filterSupportedPlacements } from '@/utils/ad-campaign-constants'
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { FacebookRoundedIcon, InstagramIcon, MessengerIcon, AudienceNetworkIcon } from '@/components/icons'
import PlatformRow from './PlatformRow'

// Icon mapping for platform options
const PLATFORM_ICONS = {
    facebook: FacebookRoundedIcon,
    instagram: InstagramIcon,
    messenger: MessengerIcon,
    audience_network: AudienceNetworkIcon,
}

// Add icons to platform options
const PlATFORM_OPTIONS = PLATFORM_PLACEMENT_OPTIONS.map((platform) => ({
    ...platform,
    icon: PLATFORM_ICONS[platform.id],
}))

// Helper function to check if Instagram Search should be disabled
const isInstagramSearchDisabled = (selectedPlacements) => {
    const instagramFeedSelected = selectedPlacements.includes('instagram:stream')
    return !instagramFeedSelected
}

const PlatformPlacementsSettings = ({
    stage,
    audience,
    metricsDataLoading,
    formatMetricValue,
    platformMetricsData,
    placementMetricsData,
    setStepContentData,
    getStepContentData,
}) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()
    const [selectedPlacements, setSelectedPlacements] = useState([])
    const [expandedPlatforms, setExpandedPlatforms] = useState(PlATFORM_OPTIONS.map((platform) => platform.id))

    // Column preferences hook
    const {
        selectedColumns,
        allMetrics,
        updateSelectedColumns,
        isLoading: allMetricsLoading,
    } = useColumnPreferences('PlatformPlacements', setStepContentData, getStepContentData)

    const loading = metricsDataLoading || allMetricsLoading

    // Handle metric replacement for a specific column index
    const handleMetricReplace = (columnIndex, newMetric) => {
        const currentMetrics = [...selectedColumns]
        currentMetrics[columnIndex] = newMetric
        updateSelectedColumns(currentMetrics)
    }

    // Direct lookup functions using raw GraphQL data
    const getMetricValue = (apiKey, platformId, placementId) => {
        if (!placementMetricsData) return null
        const targetBreakdown = `${platformId}:${placementId}`
        const metric = placementMetricsData.find(
            (m) => m.breakdownValue === targetBreakdown && m.metricApiKey === apiKey,
        )
        return metric?.value ?? null
    }

    // Get platform metric value from database
    const getPlatformMetricValue = (apiKey, platformId) => {
        if (!platformMetricsData) return null
        const metric = platformMetricsData.find((m) => m.breakdownValue === platformId && m.metricApiKey === apiKey)
        return metric?.value ?? null
    }

    // Deprecated: Use getPlatformMetricValue instead
    // Keeping for backward compatibility if needed
    const getPlatformTotal = (apiKey, platform) => {
        // Now using getPlatformMetricValue to get platform totals from database
        return getPlatformMetricValue(apiKey, platform.id)
    }

    // Helper function to render metric value, replacing "-" with MinusIcon
    const renderMetricValue = (value, column) => {
        const formattedValue = formatMetricValue(value, column, true)
        if (formattedValue === '-') {
            return (
                <div className="tw:flex tw:justify-end">
                    <MinusIcon />
                </div>
            )
        }
        return formattedValue
    }

    // Platform and placement options with hierarchical structure

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedPlacements = audienceSettings?.platform_placement_targets

    // Initialize from stored value
    useEffect(() => {
        if (storedPlacements && Array.isArray(storedPlacements)) {
            const supportedPlacements = filterSupportedPlacements(storedPlacements)
            setSelectedPlacements(supportedPlacements)
            if (supportedPlacements.length !== storedPlacements.length) {
                updateAudienceSetting(stage, audience, 'platform_placement_targets', supportedPlacements)
            }
        } else {
            // Default to all placements selected
            const defaultPlacements = PlATFORM_OPTIONS.flatMap((platform) =>
                platform.subPlacements.map((placement) => `${platform.id}:${placement.id}`),
            )
            setSelectedPlacements(defaultPlacements)
            updateAudienceSetting(stage, audience, 'platform_placement_targets', defaultPlacements)
        }
    }, [storedPlacements, stage, audience, updateAudienceSetting])

    const handlePlatformToggle = (platformId) => {
        let newSelection = [...selectedPlacements]

        // Find the platform object
        const platform = PlATFORM_OPTIONS.find((p) => p.id === platformId)

        if (platform) {
            // Toggle all sub-placements for this platform
            const subPlacementKeys = platform.subPlacements.map((sub) => `${platformId}:${sub.id}`)
            const allSubPlacementsSelected = subPlacementKeys.every((key) => selectedPlacements.includes(key))

            if (allSubPlacementsSelected) {
                newSelection = newSelection.filter((p) => !subPlacementKeys.includes(p))
            } else {
                subPlacementKeys.forEach((key) => {
                    if (!newSelection.includes(key)) newSelection.push(key)
                })
            }

            // Don't allow all placements to be deselected
            if (newSelection.length === 0) return

            setSelectedPlacements(newSelection)
            updateAudienceSetting(stage, audience, 'platform_placement_targets', newSelection)
        }
    }

    const handlePlacmentToggle = (platformId, placmentId) => {
        let newSelection = [...selectedPlacements]
        const placementKey = `${platformId}:${placmentId}`

        // Prevent Instagram Search selection if Instagram Feed is not selected
        if (platformId === 'instagram' && placmentId === 'ig_search' && isInstagramSearchDisabled(selectedPlacements)) {
            return
        }

        // Toggle individual placement
        if (newSelection.includes(placementKey)) {
            newSelection = newSelection.filter((p) => p !== placementKey)

            // If Instagram Feed is being deselected, also deselect Instagram Search
            if (platformId === 'instagram' && placmentId === 'stream') {
                newSelection = newSelection.filter((p) => p !== 'instagram:ig_search')
            }
        } else {
            newSelection.push(placementKey)
        }

        // Don't allow all placements to be deselected
        if (newSelection.length === 0) return

        setSelectedPlacements(newSelection)
        updateAudienceSetting(stage, audience, 'platform_placement_targets', newSelection)
    }

    const handleExpandToggle = (platformId) => {
        setExpandedPlatforms((prev) =>
            prev.includes(platformId) ? prev.filter((p) => p !== platformId) : [...prev, platformId],
        )
    }

    const isPlatformChecked = (platform) => {
        const subPlacementKeys = platform.subPlacements.map((sub) => `${platform.id}:${sub.id}`)
        return subPlacementKeys.every((key) => selectedPlacements.includes(key))
    }

    const isPlatformIndeterminate = (platform) => {
        const subPlacementKeys = platform.subPlacements.map((sub) => `${platform.id}:${sub.id}`)
        const selectedSubPlacements = subPlacementKeys.filter((key) => selectedPlacements.includes(key))
        return selectedSubPlacements.length > 0 && selectedSubPlacements.length < subPlacementKeys.length
    }

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs tw:pt-6">
            <div className="">
                <Table className="tw:[&_tbody_tr:nth-child(even)]:bg-[#142234]">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="tw:text-gray-200 tw:pl-3">Platform & Placements</TableHead>
                            {selectedColumns.map((column, index) => (
                                <TableHead key={column.id} className="tw:text-gray-200 tw:!text-right tw:pr-3">
                                    <MetricsPicker
                                        allMetrics={allMetrics}
                                        currentMetric={column}
                                        onMetricSelect={(metric) => handleMetricReplace(index, metric)}
                                    >
                                        <button
                                            onClick={(e) => e.stopPropagation()}
                                            className={cn(
                                                'tw:flex tw:items-center tw:justify-end tw:gap-1 hover:tw:text-white',
                                                'tw:transition-colors tw:duration-200 tw:ease-in-out tw:cursor-pointer tw:w-full',
                                            )}
                                        >
                                            <span>{column.name}</span>
                                            <div className="tw:flex tw:items-center tw:gap-2">
                                                <ChevronDown className="tw:w-4 tw:h-4" />
                                            </div>
                                        </button>
                                    </MetricsPicker>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    {loading && (
                        <TableRowSkeleton
                            rows={12}
                            showIcon={true}
                            showCheckbox={true}
                            columns={selectedColumns.length + 1}
                        />
                    )}

                    {!loading && (
                        <TableBody>
                            {PlATFORM_OPTIONS.map((platform) => (
                                <PlatformRow
                                    platform={platform}
                                    getMetricValue={getMetricValue}
                                    selectedColumns={selectedColumns}
                                    getPlatformTotal={getPlatformTotal}
                                    renderMetricValue={renderMetricValue}
                                    handleExpandToggle={handleExpandToggle}
                                    isChecked={isPlatformChecked(platform)}
                                    selectedPlacements={selectedPlacements}
                                    handlePlatformToggle={handlePlatformToggle}
                                    handlePlacmentToggle={handlePlacmentToggle}
                                    key={`platform-placment-targeting-${platform.id}`}
                                    isIndeterminate={isPlatformIndeterminate(platform)}
                                    isExpanded={expandedPlatforms.includes(platform.id)}
                                />
                            ))}
                        </TableBody>
                    )}
                </Table>
            </div>
        </div>
    )
}

export default PlatformPlacementsSettings
